import React from 'react';
import styled, { useTheme } from 'styled-components';
import { useDropzone, FileWithPath, FileRejection } from 'react-dropzone';
import { useHistory, Prompt } from 'react-router-dom';
import axios from 'axios';
import useAuth from '../../hooks/use-auth';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { ROUTES, getSubmitProjectPageTitle } from '../../routing';
import PageWithAsyncData from '../../components/PageWithAsyncData';
import PageWrapper from '../../components/PageWrapper';
import Navbar from '../../components/Navbar';
import { H1, H3, P } from '../../components/elements/Text';
import Button from '../../components/elements/Button';
import { Input, MultiLineInput } from '../../components/elements/Input';
import { Helmet } from 'react-helmet-async';
import * as CrashReporter from '../../crash-reporter';
import { useMutation } from 'react-query';
import { useTeamsProfile } from '../../hooks/use-hydra';
import utc from 'dayjs/plugin/utc';
import {
  createCompanyProjectUploadLinks,
  submitCompanyProject,
} from '../../api/teams';
import { S3UploadError, uploadToS3 } from '../../api/s3-upload';
import {
  COMPANY_PROJECT_MAX_FILE_BYTES,
  COMPANY_PROJECT_MAX_FILES,
} from '@enki/contracts/teams/writes';

dayjs.extend(utc);

const MAX_FILE_SIZE_LABEL = `${Math.round(
  COMPANY_PROJECT_MAX_FILE_BYTES / (1024 * 1024)
)} MB`;
const LIMITS_MESSAGE = `Each file can be up to ${MAX_FILE_SIZE_LABEL}, and you can upload up to ${COMPANY_PROJECT_MAX_FILES} files. For larger files, add a link to Google Drive, OneDrive or Dropbox below.`;
const GENERIC_ERROR =
  'Something went wrong. Please reach out to us to resolve the issue.';

const questions = [
  {
    label: 'Project Selection',
    question: 'Which project did you choose to work on, and why?',
  },
  {
    label: 'Project Impact',
    question:
      'What could be the impact from what you got done for your company project? How many hours per week could be saved, what capabilities have been unlocked, or any other benefits?',
  },
  {
    label: 'Assistance',
    question:
      'What kind of help did you get from your mentor related to your project? What about from your colleagues?',
  },
  {
    label: 'Conclusions',
    question:
      'Will you want to continue developing what you worked on going forward? What elements of your learning will you take forward?',
  },
];

const DeleteIcon = styled.i.attrs({
  className: 'fa-duotone fa-trash',
})`
  color: ${({ theme }) => theme.colors.textRegular};
  transform: translateY(1px);

  :hover {
    cursor: pointer;
  }
`;

// this handles a bug noticed on live deployments where learners would submit an answer with the "’" quote
// when decoding the formData on the server, the "’" is converted to an unescaped "/u0019" which subsequently breaks JSON parsing
// see https://enkilabs.slack.com/archives/C042F5UJG/p1676887609950019
const cleanInput = (input: string) => {
  return input.replace(/’/g, "'");
};

const formatFileSize = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

function submissionErrorMessage(error: unknown): string {
  if (error instanceof S3UploadError) {
    return error.status === null
      ? 'Your files could not be uploaded because the connection dropped. Check your connection and try again.'
      : `Your files could not be uploaded. ${LIMITS_MESSAGE}`;
  }
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return 'We could not reach Enki. Check your connection and try again.';
    }
    const data = error.response.data as { message?: string; error?: string };
    if (error.response.status === 400 && (data?.message || data?.error)) {
      return (data.message || data.error) as string;
    }
    if (error.response.status === 403) {
      return 'You can only submit your own company project. Please sign in again, or reach out to us.';
    }
  }
  return GENERIC_ERROR;
}

export default function SubmitProject() {
  const { getTokenSilently } = useAuth();
  const {
    value: teamsProfile,
    loading: teamsProfileLoading,
    error: teamsProfileError,
    retry: retryTeamsProfile,
  } = useTeamsProfile();
  const history = useHistory();

  const latestCohort =
    teamsProfile?.onboardedCohorts.sort(
      (a, b) =>
        dayjs.utc(b.cohort.startDate).valueOf() -
        dayjs.utc(a.cohort.startDate).valueOf()
    )[0] ?? null;

  const [initialAnswers, setInitialAnswers] = React.useState<string[]>(
    [...Array(questions.length)].map(() => '')
  );
  const [answers, setAnswers] = React.useState(
    [...Array(questions.length)].map(() => '')
  );
  const areAnswersFilledIn = answers.map((a) => a.length >= 3);

  const [initialLinks, setInitialLinks] = React.useState<string[]>([]);
  const [links, setLinks] = React.useState<string[]>([]);

  const [files, setFiles] = React.useState<FileWithPath[]>([]);
  const [submittedFileLinks, setSubmittedFileLinks] = React.useState<string[]>(
    []
  );
  const [initialSubmittedFileLinks, setInitialSubmittedFileLinks] =
    React.useState<string[]>([]);
  const [uploadStatus, setUploadStatus] = React.useState<string | null>(null);

  const onRemoveFile = React.useCallback(
    (i: number) => {
      setFiles(files.filter((_, idx) => idx !== i));
    },
    [files]
  );
  const onRemoveLink = React.useCallback(
    (i: number) => {
      setLinks(links.filter((_, idx) => idx !== i));
    },
    [links]
  );
  const onRemoveSubmittedFile = React.useCallback(
    (i: number) => {
      setSubmittedFileLinks(submittedFileLinks.filter((_, idx) => idx !== i));
    },
    [submittedFileLinks]
  );

  const onQuestionInput = React.useCallback(
    (value: string, i: number) =>
      setAnswers(answers.map((answer, idx) => (i === idx ? value : answer))),
    [answers]
  );

  const onAddLink = React.useCallback(
    (link: string) => {
      if (link) {
        setLinks([...new Set([link, ...links])]);
      }
    },
    [links]
  );

  React.useEffect(() => {
    if (!latestCohort && !teamsProfileLoading) {
      toast.error(
        'You are not part of any cohort. Please reach out to us to resolve the issue.'
      );
      history.push(ROUTES.HOME.getPath());
    }
  }, [latestCohort, history, teamsProfileLoading]);

  React.useEffect(() => {
    if (latestCohort) {
      // Prefill answers and links from latest PROJECT_UPLOAD submission
      const submission = latestCohort.milestones?.PROJECT_UPLOAD;
      if (submission) {
        if (submission.survey) {
          const prefilledAnswers = questions.map(
            (q, i) => submission.survey[i]?.answer || ''
          );
          setAnswers(prefilledAnswers);
          setInitialAnswers(prefilledAnswers);
        }
        if (submission.external_links) {
          const prefilledLinks = submission.external_links.filter(Boolean);
          setLinks(prefilledLinks);
          setInitialLinks(prefilledLinks);
        }
        // Optionally, you could show file_links as read-only info, but not as File objects
        // For files, we can't prefill File objects, so just treat as empty
        setFiles([]);
        const prefilledFileLinks = Array.isArray(submission.file_links)
          ? submission.file_links.filter(Boolean)
          : [];
        setSubmittedFileLinks(prefilledFileLinks);
        setInitialSubmittedFileLinks(prefilledFileLinks);
      }
    }
  }, [latestCohort, history]);

  const isSubmitted = !!latestCohort?.milestones?.PROJECT_UPLOAD?.submittedAt;

  const submitAnswers = useMutation({
    mutationFn: async () => {
      const cohortId = latestCohort?.cohort.id;
      const learnerId = teamsProfile?.learnerId;
      if (!cohortId || !learnerId) {
        throw new Error('Missing cohort or learner for the submission');
      }

      // Files go straight to S3; the submission only carries their keys.
      const uploadedKeys: string[] = [];
      if (files.length) {
        const token = await getTokenSilently();
        const describe = (file: FileWithPath) => ({
          name: file.name,
          size: file.size,
          contentType: file.type,
        });
        const { uploads } = await createCompanyProjectUploadLinks(token, {
          cohortId,
          learnerId,
          files: files.map(describe),
        });

        for (const [i, file] of files.entries()) {
          const onProgress = (fraction: number) =>
            setUploadStatus(
              `Uploading file ${i + 1} of ${files.length} (${Math.round(
                fraction * 100
              )}%)`
            );
          let upload = uploads[i];
          try {
            await uploadToS3({ ...upload, file, onProgress });
          } catch (error) {
            // One retry with a fresh link, after a dropped connection or an expired link.
            const retry = await createCompanyProjectUploadLinks(
              await getTokenSilently(),
              { cohortId, learnerId, files: [describe(file)] }
            );
            upload = retry.uploads[0];
            await uploadToS3({ ...upload, file, onProgress });
          }
          uploadedKeys.push(upload.key);
        }
      }

      setUploadStatus('Saving your submission');
      await submitCompanyProject(await getTokenSilently(), {
        cohortId,
        learnerId,
        body: {
          submissionType: 'PROJECT_UPLOAD',
          survey: answers.map((answer, i) => ({
            answer: cleanInput(answer),
            question: cleanInput(questions[i].question),
          })),
          links: links.map((link) => cleanInput(link)),
          retainedFileLinks: submittedFileLinks,
          uploadedKeys,
        },
      });
      setInitialAnswers([...answers]);
      setInitialLinks([...links]);
      setSubmittedFileLinks([...submittedFileLinks]);
      setInitialSubmittedFileLinks([...submittedFileLinks]);
    },
    onSuccess: async () => {
      setUploadStatus(null);
      await retryTeamsProfile();
      toast.success('Successfully submitted the project!');
      history.push(ROUTES.HOME.getPath());
    },
    onError: (err) => {
      setUploadStatus(null);
      CrashReporter.reportError(err as unknown as Error);
      toast.error(submissionErrorMessage(err));
    },
  });

  // Detect unsaved changes
  const hasUnsavedChanges = React.useMemo(() => {
    if (submitAnswers.isLoading) return false;
    const answersChanged = answers.some((a, i) => a !== initialAnswers[i]);
    const linksChanged =
      links.length !== initialLinks.length ||
      links.some((l, i) => l !== initialLinks[i]);
    const filesChanged = files.length > 0; // If any files are present, treat as unsaved
    const submittedFileLinksChanged =
      submittedFileLinks.length !== initialSubmittedFileLinks.length ||
      submittedFileLinks.some((l, i) => l !== initialSubmittedFileLinks[i]);
    return (
      answersChanged ||
      linksChanged ||
      filesChanged ||
      submittedFileLinksChanged
    );
  }, [
    answers,
    initialAnswers,
    links,
    initialLinks,
    files,
    submittedFileLinks,
    initialSubmittedFileLinks,
    submitAnswers.isLoading,
  ]);

  // beforeunload handler
  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  return (
    <PageWithAsyncData
      className={''}
      isLoading={teamsProfileLoading}
      error={teamsProfileError}
      retry={retryTeamsProfile}
    >
      <>
        <Helmet>
          <title>{getSubmitProjectPageTitle()}</title>
        </Helmet>
        <Navbar
          breadCrumbs={[
            {
              label: 'Home',
              url: ROUTES.HOME.getPath(),
            },
            {
              label: 'Submit your Company Project',
              url: null,
            },
          ]}
        />
        <PageWrapper
          style={{
            margin: '0 auto 52px',
            maxWidth: 820,
          }}
        >
          <H1>Company Project Submission</H1>
          <P>
            Please share as much detail as appropriate - all of this will be
            useful as part of the cohort report.
          </P>
          {questions.map(({ label, question }, i) => (
            <>
              <H3>{label}</H3>
              <P>{question}</P>
              <MultiLineInput
                minRows={3}
                placeholder={'Answer here...'}
                value={answers[i]}
                onChange={(e) => onQuestionInput(e.target.value, i)}
              />
            </>
          ))}
          <UploadProjectFiles
            setFiles={setFiles}
            onRemoveFile={onRemoveFile}
            files={files}
            submittedFileLinks={submittedFileLinks}
            onRemoveSubmittedFile={onRemoveSubmittedFile}
          />
          <AddExternalProjectlinks
            onRemoveLink={onRemoveLink}
            links={links}
            onAddLink={onAddLink}
          />
          <div style={{ marginTop: '52px' }}>
            <Button
              onClick={submitAnswers.mutate}
              isDisabled={!areAnswersFilledIn.every(Boolean)}
              label={isSubmitted ? 'Update Project' : 'Upload Project'}
              type="primary"
              isLoading={submitAnswers.isLoading}
            />
            {uploadStatus && (
              <P style={{ marginTop: '12px' }}>{uploadStatus}</P>
            )}
          </div>
          <Prompt
            when={hasUnsavedChanges}
            message="You have unsaved changes. Are you sure you want to leave this page?"
          />
        </PageWrapper>
      </>
    </PageWithAsyncData>
  );
}

const StyledDropzone = styled.div`
  margin-top: 32px;
  margin-bottom: 12px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.inputBackground};
  border-color: ${({ theme }) => theme.colors.bgActive};

  &:hover {
    border-color: ${({ theme }) => theme.colors.inputBorderActive};
  }
  border-width: 2px;
  border-style: dashed;
  padding: 32px 20px;
  transition: all 0.3s ease-in-out;

  text-align: center;

  p {
    margin-top: 0;
    padding-bottom: 0;
  }
`;

const UploadIcon = styled.i.attrs({
  className: 'fa-duotone fa-upload',
})`
  color: ${({ theme }) => theme.colors.textRegular};
  margin-bottom: 24px;
  font-size: 32px;
`;

function UploadProjectFiles({
  files,
  onRemoveFile,
  setFiles,
  submittedFileLinks = [],
  onRemoveSubmittedFile,
}: {
  files: FileWithPath[];
  onRemoveFile: (i: number) => void;
  setFiles: React.Dispatch<React.SetStateAction<FileWithPath[]>>;
  submittedFileLinks?: string[];
  onRemoveSubmittedFile: (i: number) => void;
}) {
  const theme = useTheme();
  const { getRootProps, getInputProps } = useDropzone({
    maxSize: COMPANY_PROJECT_MAX_FILE_BYTES,
    onDropRejected: (rejections: FileRejection[]) => {
      const tooLarge = rejections.filter(({ errors }) =>
        errors.some((e) => e.code === 'file-too-large')
      );
      toast.error(
        tooLarge.length
          ? `${tooLarge
              .map(({ file }) => file.name)
              .join(', ')} is too large. ${LIMITS_MESSAGE}`
          : LIMITS_MESSAGE
      );
    },
    onDrop: (acceptedFiles) => {
      if (!acceptedFiles.length) return;
      const room = COMPANY_PROJECT_MAX_FILES - files.length;
      if (acceptedFiles.length > room) {
        toast.error(LIMITS_MESSAGE);
      }
      const added = acceptedFiles.slice(0, Math.max(0, room));
      // Remove any submittedFileLinks with the same name as a new upload
      added.forEach((file) => {
        const fileName = 'path' in file ? file.path : file.name;
        const idx = submittedFileLinks.findIndex(
          (url) => (url.split('/').pop() || url) === fileName
        );
        if (idx !== -1) {
          onRemoveSubmittedFile(idx);
        }
      });
      setFiles([...files, ...added]);
    },
  });
  return (
    <>
      <H3>Project Files</H3>
      <P>{LIMITS_MESSAGE}</P>
      <StyledDropzone {...getRootProps({ className: 'dropzone' })}>
        <input {...getInputProps()} />
        <UploadIcon />
        <P style={{ color: theme.colors.inputTextPlaceholder }}>
          Drag and drop your project files here, or click to select them from
          your computer.
        </P>
      </StyledDropzone>
      <P>
        {files.length || submittedFileLinks.length
          ? 'Uploaded Files'
          : "You haven't uploaded any files"}
      </P>
      <ol style={{ marginTop: '12px' }}>
        {files.map((file, i) => (
          <li key={file.path}>
            {file.path} ({formatFileSize(file.size)}) -{' '}
            <DeleteIcon onClick={() => onRemoveFile(i)} />
          </li>
        ))}
        {submittedFileLinks.map((url, i) => {
          const fileName = url.split('/').pop() || url;
          return (
            <li key={url}>
              {fileName} -{' '}
              <DeleteIcon onClick={() => onRemoveSubmittedFile(i)} />
            </li>
          );
        })}
      </ol>
    </>
  );
}

function AddExternalProjectlinks({
  links,
  onRemoveLink,
  onAddLink,
}: {
  links: string[];
  onRemoveLink: (index: number) => void;
  onAddLink: (link: string) => void;
}) {
  const [newLink, setNewLink] = React.useState('');
  // simple URL regex validator https://regexr.com/39p0t
  const isLinkValid =
    /(https?:\/\/)?([\w-])+\.{1}([a-zA-Z]{2,63})([/\w-]*)*\/?\??([^#\n\r]*)?#?([^\n\r]*)/.test(
      newLink
    );
  return (
    <>
      <H3>External Links (optional)</H3>
      <P>
        Alternatively, if you have relevant materials hosted elsewhere, feel
        free to add their links below.
      </P>
      <Input
        type="link"
        placeholder={
          'https://dataflix.looker.com/embed/explore/bike_data/bike_data?qid=bV0UdBHGRkuq987hL4SZAq&origin_space=14&toggle=vis'
        }
        value={newLink}
        onChange={(e) => setNewLink(e.target.value)}
        onSubmit={() => onAddLink(newLink)}
        canSubmit={isLinkValid}
      />
      <P>{links.length ? 'External Links' : "You haven't added any links"}</P>
      <ol style={{ marginTop: '12px' }}>
        {links.map((link, i) => (
          <li key={link}>
            <a href={link} target="_blank" rel="noopener noreferrer">
              {link}
            </a>
            {' - '}
            <DeleteIcon onClick={() => onRemoveLink(i)} />
          </li>
        ))}
      </ol>
    </>
  );
}
